import React, { useEffect, useMemo } from 'react'
import Tab from '@mui/material/Tab'

import { Link, useLocation } from 'react-router-dom'
import { Box, SxProps } from '@mui/material'
import {
  getActiveTabNavItem,
  getVisibleTabNavItems,
  TabNavItem,
  useTabNavContext,
} from '../../utils/providers/tabNavContext'

export type Item = TabNavItem

export interface TabNavLinkProps {
  items: Item[]
}

export const useTabNavLink = (
  items: Item[],
  sx?: SxProps
): { currentRouteIndex: number | false; displayableItems: React.ReactNode[] } => {
  const location = useLocation()

  return useMemo(() => {
    const filteredItems = getVisibleTabNavItems(items)

    const activeItem = getActiveTabNavItem(filteredItems, location.pathname)
    const maybeIndex = activeItem ? filteredItems.indexOf(activeItem) : -1
    const currentRouteIndex = maybeIndex > -1 ? maybeIndex : false

    const displayableItems = filteredItems.map((tab, index) => {
      const isActive = index === maybeIndex
      // Only block the click when already on the list page; on nested routes (e.g. users/:id)
      // the active tab should navigate back to its list.
      const isOnListPage = isActive && location.pathname === tab.to

      return (
        <Tab
          sx={sx}
          key={index}
          disableRipple
          label={tab.label}
          component={Link}
          to={isOnListPage ? { pathname: tab.to, search: location.search } : tab.to}
          onClick={
            isOnListPage
              ? (event: React.MouseEvent) => {
                  event.preventDefault()
                }
              : undefined
          }
        />
      )
    })

    return { currentRouteIndex, displayableItems }
  }, [items, location.pathname, location.search, sx])
}

const TabNavLink: React.FC<TabNavLinkProps> = ({ children, items }) => {
  const { registerPrimaryItems } = useTabNavContext()

  useEffect(() => {
    registerPrimaryItems(items)
  }, [items, registerPrimaryItems])

  useEffect(() => {
    return () => registerPrimaryItems([])
  }, [registerPrimaryItems])

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default TabNavLink
